import { useCallback, useEffect, useState } from "react";
import type { Photo } from "@/types";
import { cacheManager } from "../services/ImageCacheManager";

/**
 * Hook return type
 */
export interface UsePreloadImagesReturn {
  isImageCached: (url: string) => boolean;
  preloadImage: (url: string) => void;
  getCacheStats: () => { total: number; loaded: number; loading: number };
  cacheSize: number;
  bulkPreload: (
    urls: string[],
    onProgress?: (loaded: number, total: number) => void
  ) => Promise<void>;
  cancelBulkPreload: () => void;
}

/**
 * Hook for managing image preloading with LRU cache
 *
 * Uses ImageCacheManager singleton for centralized state management.
 * This avoids race conditions and unintended sharing between instances.
 *
 * Features:
 * - Global cache via singleton (persists between unmount/remount)
 * - Max 1000 images cached
 * - LRU eviction when cache is full (disabled after bulk preload)
 * - Directional preload based on velocity
 *
 * @param photos - Array of photos to preload from
 * @param currentIndex - Current display index
 * @param velocity - Current playback velocity (negative = backwards)
 */
export function usePreloadImages(
  photos: Photo[],
  currentIndex: number,
  velocity: number
): UsePreloadImagesReturn {
  const [cacheSize, setCacheSize] = useState(cacheManager.getCacheSize());

  /**
   * Check if an image URL is cached and loaded
   */
  const isImageCached = useCallback((url: string): boolean => {
    return cacheManager.isImageCached(url);
  }, []);

  /**
   * Preload a single image
   */
  const preloadImage = useCallback((url: string): void => {
    cacheManager.preloadImage(url, () => {
      setCacheSize(cacheManager.getCacheSize());
    });
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback((): {
    total: number;
    loaded: number;
    loading: number;
  } => {
    return cacheManager.getCacheStats();
  }, []);

  /**
   * Cancel any ongoing bulk preload
   */
  const cancelBulkPreload = useCallback(() => {
    cacheManager.cancelBulkPreload();
  }, []);

  /**
   * Bulk preload multiple images with progress callback
   */
  const bulkPreload = useCallback(
    async (
      urls: string[],
      onProgress?: (loaded: number, total: number) => void
    ): Promise<void> => {
      await cacheManager.bulkPreload(urls, (loaded, total) => {
        onProgress?.(loaded, total);
        setCacheSize(cacheManager.getCacheSize());
      });
    },
    []
  );

  /**
   * Directional preload based on velocity
   * Always runs to ensure images ahead of playback are cached
   */
  useEffect(() => {
    if (photos.length === 0) return;

    const absVelocity = Math.abs(velocity);

    // Calculate dynamic buffer size based on velocity
    const bufferSize = Math.min(
      cacheManager.MAX_BUFFER_SIZE,
      cacheManager.BASE_BUFFER_SIZE + Math.floor(absVelocity / 10)
    );

    // Calculate ahead/behind split based on velocity direction
    let aheadRatio: number;
    if (velocity > 5) {
      aheadRatio = 0.8;
    } else if (velocity < -5) {
      aheadRatio = 0.2;
    } else {
      aheadRatio = 0.5;
    }

    const aheadCount = Math.floor(bufferSize * aheadRatio);
    const behindCount = bufferSize - aheadCount;

    // Preload images ahead (positive direction)
    // Use thumb400 for better playback quality, fallback to thumb200
    for (let i = 1; i <= aheadCount; i++) {
      const index = currentIndex + i;
      if (index >= 0 && index < photos.length) {
        const url = photos[index].urls.thumb400 || photos[index].urls.thumb200;
        if (url) preloadImage(url);
      }
    }

    // Preload images behind (negative direction)
    for (let i = 1; i <= behindCount; i++) {
      const index = currentIndex - i;
      if (index >= 0 && index < photos.length) {
        const url = photos[index].urls.thumb400 || photos[index].urls.thumb200;
        if (url) preloadImage(url);
      }
    }

    // Always preload current image
    if (currentIndex >= 0 && currentIndex < photos.length) {
      const currentUrl = photos[currentIndex].urls.thumb400 || photos[currentIndex].urls.thumb200;
      if (currentUrl) preloadImage(currentUrl);
    }

    // Update cache size state
    setCacheSize(cacheManager.getCacheSize());
  }, [photos, currentIndex, velocity, preloadImage]);

  return {
    isImageCached,
    preloadImage,
    getCacheStats,
    cacheSize,
    bulkPreload,
    cancelBulkPreload,
  };
}
